import AWS from 'aws-sdk';
import { S3_CONFIG } from '../api/config';

// Configure AWS SDK
const s3Config = {
  region: S3_CONFIG.region,
  signatureVersion: S3_CONFIG.signatureVersion
};

// Only add endpoint and credentials for local development (MinIO)
if (S3_CONFIG.endpoint && S3_CONFIG.endpoint.includes('localhost')) {
  s3Config.endpoint = S3_CONFIG.endpoint;
  s3Config.accessKeyId = S3_CONFIG.accessKeyId;
  s3Config.secretAccessKey = S3_CONFIG.secretAccessKey;
  s3Config.s3ForcePathStyle = true; // Required for MinIO
} else {
  // In production, use IAM roles and standard AWS S3
  s3Config.s3ForcePathStyle = false;
  // Don't set accessKeyId/secretAccessKey - let AWS SDK use IAM roles or environment credentials
}

const s3 = new AWS.S3(s3Config);

/**
 * Get a signed URL for an S3 object
 * @param {string} key - The S3 object key
 * @param {number} expiresIn - URL expiration time in seconds (default: 3600)
 * @returns {string} Signed URL
 */
export const getSignedUrl = (key, expiresIn = 3600) => {
  try {
    // In production, we should prefer API-generated presigned URLs
    // This function should only be used as a fallback
    if (process.env.NODE_ENV === 'production' && (!S3_CONFIG.endpoint || !S3_CONFIG.endpoint.includes('localhost'))) {
      console.warn('getSignedUrl fallback used in production for key:', key);
      console.warn('API should provide presigned URLs to avoid client-side S3 configuration');
    }
    
    const url = s3.getSignedUrl('getObject', {
      Bucket: S3_CONFIG.bucketName,
      Key: key,
      Expires: expiresIn
    });
    
    // Log the generated URL in development for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log('Generated signed URL for key:', key, 'URL:', url);
    }
    
    return url;
  } catch (error) {
    console.error('Error generating signed URL for key:', key, 'Error:', error);
    console.error('S3 Config:', {
      bucketName: S3_CONFIG.bucketName,
      region: S3_CONFIG.region,
      endpoint: S3_CONFIG.endpoint,
      hasCredentials: !!(S3_CONFIG.accessKeyId && S3_CONFIG.secretAccessKey)
    });
    return null;
  }
};

/**
 * Upload a file to S3
 * @param {File} file - The file to upload
 * @param {string} key - The S3 object key
 * @param {string} modelName - The model name for folder structure
 * @param {string} folder - The folder (raw, filtered, manual, cropped)
 * @returns {Promise<string>} S3 object key
 */
export const uploadFile = async (file, key, modelName, folder = 'raw') => {
  try {
    // Ensure the key follows the pattern bucket/{model_name}/raw/
    const formattedKey = `${modelName}/${folder}/${key.split('/').pop()}`;
    
    const params = {
      Bucket: S3_CONFIG.bucketName,
      Key: formattedKey,
      Body: file,
      ContentType: file.type
    };
    
    const result = await s3.upload(params).promise();
    return result.Key;
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    throw error;
  }
};

/**
 * Copy an S3 object from one location to another
 * @param {string} sourceKey - Source S3 object key
 * @param {string} targetKey - Target S3 object key
 * @returns {Promise<string>} Target S3 object key
 */
export const s3Upload = async (sourceKey, targetKey) => {
  try {
    const params = {
      Bucket: S3_CONFIG.bucketName,
      CopySource: `${S3_CONFIG.bucketName}/${sourceKey}`,
      Key: targetKey
    };
    
    const result = await s3.copyObject(params).promise();
    return targetKey;
  } catch (error) {
    console.error('Error copying S3 object:', error);
    throw error;
  }
};

/**
 * Upload text content directly to S3
 * @param {string} key - The S3 object key
 * @param {string} textContent - The text content to save
 * @returns {Promise<Object>} S3 upload result
 */
export const uploadTextContent = async (key, textContent) => {
  try {
    const params = {
      Bucket: S3_CONFIG.bucketName,
      Key: key,
      Body: textContent,
      ContentType: 'text/plain',
      ContentEncoding: 'UTF-8'
    };
    
    const result = await s3.putObject(params).promise();
    console.log('Saved text content to S3:', key);
    return result;
  } catch (error) {
    console.error('Error uploading text content to S3:', error);
    throw error;
  }
};

/**
 * Delete an S3 object
 * @param {string} key - The S3 object key to delete
 * @returns {Promise<boolean>} Success status
 */
export const s3Delete = async (key) => {
  try {
    const params = {
      Bucket: S3_CONFIG.bucketName,
      Key: key
    };
    
    await s3.deleteObject(params).promise();
    return true;
  } catch (error) {
    console.error('Error deleting S3 object:', error);
    throw error;
  }
};

/**
 * List objects in an S3 folder
 * @param {string} prefix - The S3 folder prefix
 * @returns {Promise<Array>} List of S3 objects
 */
export const listObjects = async (prefix) => {
  try {
    const params = {
      Bucket: S3_CONFIG.bucketName,
      Prefix: prefix
    };
    
    const result = await s3.listObjectsV2(params).promise();
    return result.Contents || [];
  } catch (error) {
    console.error('Error listing S3 objects:', error);
    throw error;
  }
};
